import { useEffect, useState } from "react";

import { BN_ZERO, bigNumberify } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { fetchReferralCodes, fetchReferrerStats, fetchTraderCode, fetchCheckCodes } from "lib/indexer/client";
import { BESC } from "config/chains";

import type { CodeOwnershipInfo } from "../types";
import { decodeReferralCode } from "../utils";

const ACTIVE_CHAINS = [BESC];

export function useUserCodesOnAllChain(account) {
  const [data, setData] = useState<{ [chainId: number]: { [code: string]: any } } | null>(null);

  useEffect(() => {
    if (!account) return;

    async function main() {
      const codesRes = await fetchReferralCodes(account.toLowerCase()).catch(() => ({ codes: [] }));
      const codes = codesRes.codes.map((c) => c.code);

      const checkResult = codes.length > 0
        ? await fetchCheckCodes(codes).catch(() => ({}))
        : {};

      const codeOwnersOnBesc: CodeOwnershipInfo[] = codes.map((code) => {
        const info = checkResult[code] ?? { owner: null, isTaken: false };
        return {
          code,
          codeString: decodeReferralCode(code),
          owner: info.owner ?? undefined,
          isTaken: info.isTaken,
          isTakenByCurrentUser: info.owner ? info.owner.toLowerCase() === account.toLowerCase() : false,
        };
      });

      setData({
        [BESC]: codeOwnersOnBesc.reduce((acc, cv) => {
          acc[cv.code] = cv;
          return acc;
        }, {}),
      });
    }

    main();
  }, [account]);

  return data;
}

export function useReferralsData(chainId, account) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const ownerOnOtherChain = useUserCodesOnAllChain(account);

  useEffect(() => {
    if (!chainId || !account) {
      setLoading(false);
      return;
    }

    setLoading(true);

    async function load() {
      const [codesRes, referrerStatsRes, traderRes] = await Promise.all([
        fetchReferralCodes(account.toLowerCase()).catch(() => ({ codes: [] })),
        fetchReferrerStats(account.toLowerCase()).catch(() => null),
        fetchTraderCode(account.toLowerCase()).catch(() => null),
      ]);

      const codes = codesRes.codes.map((c) => decodeReferralCode(c.code));

      const zeroData = { volume: BN_ZERO, totalRebateUsd: BN_ZERO, discountUsd: BN_ZERO, affiliateRebateUsd: BN_ZERO };
      const makeStat = (referralCode, s) => ({
        referralCode,
        trades: s?.trades ?? 0,
        tradedReferralsCount: s?.traders ?? 0,
        registeredReferralsCount: s?.traders ?? 0,
        volume: bigNumberify(String(Math.round(s?.volume || 0))) ?? BN_ZERO,
        totalRebateUsd: BN_ZERO,
        discountUsd: BN_ZERO,
        affiliateRebateUsd: BN_ZERO,
        ownerOnOtherChain: ownerOnOtherChain?.[chainId]?.[referralCode],
        v1Data: { ...zeroData },
        v2Data: { ...zeroData },
      });

      // Index activity stats by decoded code, then list EVERY owned code — a
      // freshly created code with no trades yet must still show (with zeros),
      // otherwise the affiliate thinks their code was "forgotten".
      const statsByCode = new Map(
        (referrerStatsRes?.codeStats ?? EMPTY_ARRAY).map((s) => [decodeReferralCode(s.code), s])
      );
      const ownedCodes = Array.from(
        new Set([...codes, ...((referrerStatsRes?.codes ?? EMPTY_ARRAY).map((c) => decodeReferralCode(c)))])
      );
      const referrerTotalStats = ownedCodes.map((rc) => makeStat(rc, statsByCode.get(rc)));

      const cumulativeStats = referrerTotalStats.reduce(
        (acc, cv) => {
          acc.totalRebateUsd = acc.totalRebateUsd + cv.totalRebateUsd;
          acc.volume = acc.volume + cv.volume;
          acc.discountUsd = acc.discountUsd + cv.discountUsd;
          acc.trades = acc.trades + cv.trades;
          acc.tradedReferralsCount = acc.tradedReferralsCount + cv.tradedReferralsCount;
          acc.registeredReferralsCount = acc.registeredReferralsCount + cv.registeredReferralsCount;
          return acc;
        },
        {
          totalRebateUsd: BN_ZERO,
          volume: BN_ZERO,
          discountUsd: BN_ZERO,
          trades: 0,
          tradedReferralsCount: 0,
          registeredReferralsCount: 0,
          affiliateRebateUsd: BN_ZERO,
        }
      );

      setData({
        rebateDistributions: [],
        discountDistributions: [],
        referrerTotalStats,
        referrerTierInfo: null,
        referrerLastDayStats: [],
        cumulativeStats,
        codes,
        referralTotalStats: {
          volume: BN_ZERO,
          discountUsd: BN_ZERO,
        },
        traderReferralCode: traderRes?.code ? decodeReferralCode(traderRes.code) : null,
      });
    }

    load()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [chainId, account, ownerOnOtherChain]);

  return { data: data || null, loading };
}
